import { useMemo, useState, useEffect } from 'react';
import { TapLevel } from '@/domain/types';
import { BONUS_ARC_SIZE } from '@/domain/constants';

interface BonusArcProps {
  level: TapLevel;
  isActive: boolean;
  canPlay: boolean; // Sessions available
  bonusTime: number;
  progress: number; // 0-1 progress towards filling the arc
}

// Upper hemisphere arc (spec: not full ring, usually upper half)
const ARC_START_ANGLE = -180; // Start from left
const ARC_END_ANGLE = 0;      // End at right (180° arc = upper hemisphere)
const ARC_SPAN = ARC_END_ANGLE - ARC_START_ANGLE; // 180 degrees

// Number of segments in the arc
const SEGMENT_COUNT = 12; // Fewer segments for half-circle
const SEGMENT_GAP_DEGREES = 3;
const SEGMENT_ANGLE = (ARC_SPAN - SEGMENT_COUNT * SEGMENT_GAP_DEGREES) / SEGMENT_COUNT;

// Color interpolation from cyan to orange
function interpolateColor(t: number): string {
  // Cyan (#00bfff) -> Orange (#ff6a00)
  const r = Math.round(0 + t * 255);
  const g = Math.round(191 - t * 85);
  const b = Math.round(255 - t * 255);
  return `rgb(${r}, ${g}, ${b})`;
}

// Create SVG arc path
function createArcPath(
  cx: number,
  cy: number,
  radius: number,
  startAngle: number,
  endAngle: number
): string {
  const start = polarToCartesian(cx, cy, radius, endAngle);
  const end = polarToCartesian(cx, cy, radius, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';
  return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`;
}

function polarToCartesian(cx: number, cy: number, radius: number, angleDegrees: number) {
  const angleRadians = ((angleDegrees - 90) * Math.PI) / 180;
  return {
    x: cx + radius * Math.cos(angleRadians),
    y: cy + radius * Math.sin(angleRadians),
  };
}

export function BonusArc({ level, isActive, canPlay, progress }: BonusArcProps) {
  const center = BONUS_ARC_SIZE / 2;
  const radius = (BONUS_ARC_SIZE - 24) / 2;

  // Track previous level for flash animation on level-up
  const [prevLevel, setPrevLevel] = useState<TapLevel>(1);
  const [showFlash, setShowFlash] = useState(false);
  const [showBonusTime, setShowBonusTime] = useState<number | null>(null);

  useEffect(() => {
    if (level > prevLevel && isActive) {
      // Level increased - trigger flash and show bonus time
      setShowFlash(true);
      const bonusAmount = level === 2 ? 1.0 : level === 3 ? 1.3 : 0;
      if (bonusAmount > 0) setShowBonusTime(bonusAmount);

      setTimeout(() => setShowFlash(false), 300);
      setTimeout(() => setShowBonusTime(null), 1500);
    }
    setPrevLevel(level);
  }, [level, isActive, prevLevel]);

  // Calculate active segments based on continuous progress (0-1)
  const activeSegments = useMemo(() => {
    if (!isActive) return 0;
    // Clamp progress between 0 and 1, then map to segment count
    const clampedProgress = Math.max(0, Math.min(1, progress));
    return Math.floor(clampedProgress * SEGMENT_COUNT);
  }, [isActive, progress]);

  // Generate segment paths - upper hemisphere only
  const segments = useMemo(() => {
    const segs = [];
    for (let i = 0; i < SEGMENT_COUNT; i++) {
      // Start from ARC_START_ANGLE (-180) for upper hemisphere
      const startAngle = ARC_START_ANGLE + i * (SEGMENT_ANGLE + SEGMENT_GAP_DEGREES);
      const endAngle = startAngle + SEGMENT_ANGLE;
      const colorT = i / (SEGMENT_COUNT - 1);
      segs.push({
        path: createArcPath(center, center, radius, startAngle, endAngle),
        color: interpolateColor(colorT),
        index: i,
      });
    }
    return segs;
  }, [center, radius]);

  // Get segment color based on level (spec: L1=cold blue, L2=brighter, L3=gradient)
  const getSegmentColor = (seg: { color: string; index: number }) => {
    if (level === 1) return '#00bfff'; // Cold blue only for level 1
    if (level === 2) return '#00d4ff'; // Brighter cyan for level 2
    return seg.color; // Gradient cyan→orange for level 3
  };

  // Glow intensity based on level (spec: L1 no glow, L2/L3 have glow)
  const useGlow = level >= 2;

  return (
    <div
      className="absolute flex items-center justify-center"
      style={{
        width: BONUS_ARC_SIZE,
        height: BONUS_ARC_SIZE,
      }}
    >
      <svg
        width={BONUS_ARC_SIZE}
        height={BONUS_ARC_SIZE}
        className="absolute"
        style={{ transform: 'rotate(-90deg)' }}
      >
        <defs>
          {/* Glow filter for active segments */}
          <filter id="segment-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="2" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Inactive background segments */}
        {segments.map((seg) => (
          <path
            key={`bg-${seg.index}`}
            d={seg.path}
            fill="none"
            stroke="rgba(255, 255, 255, 0.08)"
            strokeWidth="6"
            strokeLinecap="round"
          />
        ))}

        {/* Active colored segments */}
        {segments.map(
          (seg) =>
            seg.index < activeSegments && (
              <path
                key={`active-${seg.index}`}
                d={seg.path}
                fill="none"
                stroke={getSegmentColor(seg)}
                strokeWidth="6"
                strokeLinecap="round"
                filter={useGlow ? 'url(#segment-glow)' : undefined}
                style={{
                  opacity: isActive ? (showFlash ? 1.5 : 1) : 0,
                  transition: showFlash ? 'none' : 'opacity 0.3s ease-out',
                }}
              />
            )
        )}
      </svg>

      {/* Flash overlay on level-up */}
      {showFlash && (
        <div
          className="absolute inset-0 rounded-full"
          style={{
            background: `radial-gradient(circle, ${level === 3 ? 'rgba(255, 106, 0, 0.4)' : 'rgba(0, 255, 255, 0.4)'} 0%, transparent 70%)`,
            animation: 'pulse 0.3s ease-out',
          }}
        />
      )}

      {/* Diamond marker at top center of arc */}
      <div
        className="absolute -top-1 flex items-center justify-center"
        style={{
          opacity: isActive || canPlay ? 1 : 0.4,
          filter:
            isActive || canPlay
              ? `drop-shadow(0 0 6px rgba(0, 255, 255, 0.8))`
              : 'none',
        }}
      >
        <div
          className="h-4 w-4 transition-all duration-300"
          style={{
            transform: 'scaleY(1.5) rotate(45deg)',
            background: 'transparent',
            border: isActive
              ? level === 3
                ? '2px solid #ff6a00'
                : level === 2
                  ? '2px solid #00d4ff'
                  : '2px solid #00bfff'
              : canPlay
                ? '2px solid #00bfff'  // Cyan when sessions available
                : '2px solid #4a5568', // Gray only when no sessions
            boxShadow:
              isActive || canPlay
                ? '0 0 10px rgba(0, 191, 255, 0.6)'
                : 'none',
          }}
        />
      </div>

      {/* Floating +time indicator - only shows on level transition per spec */}
      {showBonusTime !== null && (
        <div className="absolute -top-8 animate-bounce rounded-full bg-accent-gold/20 px-3 py-1 text-xs font-semibold text-accent-gold glow-gold">
          +{showBonusTime.toFixed(1)}s
        </div>
      )}
    </div>
  );
}
